/* File tree rendering + data loading (files, sync status, health) */

import { state, signals, refs, esc, escAttr, getSyncStatus, syncBadgeHtml, fileIconSvg, folderIconSvg, setPanelLoading,
         isArtifactPinned, togglePinArtifact } from './state.js';
import { effect } from './store.js';
import { api } from './api.js';
import { confirm, confirmPullFromCloud, confirmPushToCloud, showUsageInfo, showUsagePushPrompt } from './dialogs.js';
import { openDiffForScript, showDiffPanel } from './diff-panel.js';
import { openFileTab, renderWorkspaceTabs, refreshTabContent } from './search.js';
import { openScaffoldDialog } from './scaffold.js';
import { renderSyncPanel, updateStatusBarSync } from './sync-panel.js';
import { notify } from './toast.js';
import * as selection from './selection.js';

/**
 * Maps the sync-status `kind` string (matches local folder names) to the
 * apiKind segment used by /api/sync/{kind}/... endpoints.
 */
export const KIND_TO_API = {
  'global-scripts': 'scripts',
  'custom-calculations': 'calculations',
  'ui-templates': 'templates',
};

const SCRIPT_TYPE_TITLES = {
  E: "Event-bound script — attached to one or more global script events",
  M: "Module — importable via 'import {name}'",
  R: "REST-exposed script — callable as a custom REST endpoint",
};

function templateChipHtml(name) {
  const meta = state.templateMeta?.[name];
  if (!meta || (!meta.pageName && !meta.slotName)) return "";
  const slotShort = (meta.slotName || "").replace(/^Templates\//, "");
  const text = `${meta.pageName} › ${slotShort}`;
  return `<span class="template-slot-chip" data-name="${escAttr(name)}" title="${escAttr(text)}">${esc(text)}</span>`;
}

function templateOverrideHtml(name, isSystemFile) {
  if (isSystemFile) return `<span class="template-override template-override-sys" title="System reference (read-only)">[sys]</span>`;
  const meta = state.templateMeta?.[name];
  if (!meta) return "";
  if (meta.isOverride) return `<span class="template-override template-override-modifies" title="Customizes a system default">◐</span>`;
  return `<span class="template-override template-override-pure" title="Pure custom (no system equivalent)">●</span>`;
}

/**
 * Compute the (count, rollup) pair for a top-level artifact section.
 * Returns null for non-artifact folders (or when there is no data).
 */
function sectionHeaderInfo(node) {
  if (node.type !== "folder") return null;
  const children = (node.children || []).filter(c =>
    c.type === "file" && c.name !== "properties" && !c.name.endsWith(".system.html"));
  const count = children.length;

  if (node.name === "global-scripts") {
    let unattached = 0;
    for (const c of children) {
      const n = c.name.replace(/\.py$/, "");
      const meta = state.scriptMeta?.[n];
      if (!meta || !meta.type) unattached++;
    }
    const rollup = unattached > 0 ? `${unattached} unattached` : "";
    return { count, rollup };
  }
  if (node.name === "custom-calculations") {
    let drift = 0;
    for (const c of children) {
      const n = c.name.replace(/\.py$/, "");
      const s = getSyncStatus("custom-calculations", n);
      if (s && s.direction !== "in_sync") drift++;
    }
    const rollup = drift > 0 ? `↑${drift}` : "";
    return { count, rollup };
  }
  if (node.name === "ui-templates") {
    let custom = 0;
    for (const c of children) {
      if (!c.name.endsWith(".system.html") && c.name.endsWith(".html")) custom++;
    }
    const rollup = custom > 0 ? `${custom} custom` : "";
    return { count, rollup };
  }
  if (node.name === "quote-tables") {
    return { count, rollup: "" };
  }
  return null;
}

/**
 * Derive the tenant-relative file path for a syncable item.
 * Returns null for kinds (like quote-tables) that are folders.
 */
export function pathFromSyncable(apiKind, name) {
  if (apiKind === 'scripts') return `global-scripts/${name}.py`;
  if (apiKind === 'calculations') return `custom-calculations/${name}.py`;
  if (apiKind === 'templates') return `ui-templates/${name}.html`;
  return null;  // quote-tables etc. are folders — per-file refresh doesn't apply
}

/**
 * Pull a syncable item from cloud with toast feedback + editor refresh.
 * Reused by file-tree, diff-panel, pipeline, sync-panel pull flows.
 */
export async function pullWithFeedback(apiKind, name, opts = {}) {
  const t = notify(`Pulling ${name}…`, 'progress');
  try {
    await api.syncPull(apiKind, name);
    const path = pathFromSyncable(apiKind, name);
    if (path) refreshTabContent(path);
    await loadSyncStatuses(true /* force-refresh server compare */);
    if (opts.reloadTree !== false) await loadFileTree();
    t.resolve(`Pulled ${name}`, 'success');
    return true;
  } catch (e) {
    t.resolve(`Pull failed: ${e.message}`, 'error');
    return false;
  }
}

// Modules that can't import this file directly (circular — e.g. monaco-editor)
// dispatch `sync-refresh-required` on the window to trigger a full status
// refresh. Registered once at module load.
window.addEventListener('sync-refresh-required', () => {
  loadSyncStatuses(true).catch(err => console.warn('sync refresh failed', err));
});

/**
 * Push a syncable item to cloud with toast feedback.
 * loadSyncStatuses already re-renders the tree; no extra renderFileTree needed.
 */
export async function pushWithFeedback(apiKind, name) {
  const t = notify(`Pushing ${name}…`, 'progress');
  try {
    await api.syncPush(apiKind, name);
    await loadSyncStatuses(true);
    t.resolve(`Pushed ${name}`, 'success');
    return true;
  } catch (e) {
    t.resolve(`Push failed: ${e.message}`, 'error');
    return false;
  }
}

/**
 * Bulk pull/push helper: runs all items in parallel, reports a single toast
 * with aggregate success/failure counts, then forces a status refresh.
 *
 * `op` is 'pull' or 'push' — selects the api call and verb in the toast.
 * `items` is an array of `{kind, name}` from state.syncStatuses; `kind` is
 * the local folder name (e.g. 'global-scripts') which we map through
 * KIND_TO_API to the apiKind segment.
 */
export async function bulkStash(items, { reloadTree = true } = {}) {
  if (!items.length) return { ok: 0, fail: 0 };
  const t = notify(`Stashing ${items.length} item(s)…`, 'progress');
  const results = await Promise.allSettled(
    items.map(s => api.syncStash(KIND_TO_API[s.kind] || s.kind, s.name))
  );
  const ok = results.filter(r => r.status === 'fulfilled').length;
  const fail = results.length - ok;
  for (let i = 0; i < results.length; i++) {
    if (results[i].status === 'rejected') {
      console.warn('stash failed:', items[i].name, results[i].reason);
    }
  }
  await loadSyncStatuses(true);
  if (reloadTree) await loadFileTree();
  t.resolve(
    fail ? `Stashed ${ok}, ${fail} failed` : `Stashed ${ok} item(s)`,
    fail ? 'error' : 'success',
  );
  return { ok, fail };
}

export async function bulkSync(op, items, { reloadTree = false } = {}) {
  if (!items.length) return { ok: 0, fail: 0 };
  const verb = op === 'pull' ? 'Pulling' : 'Pushing';
  const pastVerb = op === 'pull' ? 'Pulled' : 'Pushed';
  const t = notify(`${verb} ${items.length} item(s)…`, 'progress');
  const apiCall = op === 'pull' ? api.syncPull : api.syncPush;
  const results = await Promise.allSettled(
    items.map(s => apiCall(KIND_TO_API[s.kind] || s.kind, s.name))
  );
  const ok = results.filter(r => r.status === 'fulfilled').length;
  const fail = results.length - ok;
  for (let i = 0; i < results.length; i++) {
    if (results[i].status === 'rejected') {
      console.warn(`${op} failed:`, items[i].name, results[i].reason);
    }
  }
  await loadSyncStatuses(true);
  if (reloadTree) await loadFileTree();
  t.resolve(
    fail ? `${pastVerb} ${ok}, ${fail} failed` : `${pastVerb} ${ok} item(s)`,
    fail ? 'error' : 'success',
  );
  return { ok, fail };
}

// ---------------------------------------------------------------------------
// Syncable file detection
// ---------------------------------------------------------------------------

const SYNCABLE_FOLDERS = ["global-scripts", "custom-calculations", "ui-templates", "quote-tables"];

/**
 * Canonical path → syncable-info mapping. Single source of truth for:
 *   - direct file sync (scripts/calculations/templates)
 *   - nested files inside a quote-table folder (resolves to the parent table)
 *
 * `opts.includeQuoteTable` — if false, only direct file kinds are returned.
 * `opts.skipProperties` — treats `.../properties/...` paths as non-syncable.
 *
 * Returns { kind, name, apiKind } or null.
 */
export function syncInfoFromPath(path, opts = {}) {
  const { includeQuoteTable = true, skipProperties = true } = opts;
  if (!path) return null;
  if (path.endsWith(".system.html")) return null;   // read-only reference
  if (path.startsWith("global-scripts/") && path.endsWith(".py")) {
    return { kind: "global-scripts", name: path.split("/").pop().replace(/\.py$/, ""), apiKind: "scripts" };
  }
  if (path.startsWith("custom-calculations/") && path.endsWith(".py")) {
    return { kind: "custom-calculations", name: path.split("/").pop().replace(/\.py$/, ""), apiKind: "calculations" };
  }
  if (path.startsWith("ui-templates/") && path.endsWith(".html")) {
    return { kind: "ui-templates", name: path.split("/").pop().replace(/\.html$/, ""), apiKind: "templates" };
  }
  if (skipProperties && (path.includes("/properties/") || path.endsWith("/properties.json"))) {
    return null;
  }
  if (includeQuoteTable && path.startsWith("quote-tables/")) {
    const parts = path.split("/");
    // Only the folder (quote-tables/{TableName}) is directly syncable; nested
    // files map to the parent table so the caller can badge/pull/push at that
    // granularity.
    if (parts.length >= 2) return { kind: "quote-tables", name: parts[1], apiKind: "quote-tables" };
  }
  return null;
}

function getSyncableInfo(node) {
  // File-tree rows pass a node; only the direct file kinds count as "own"
  // sync targets here — quote-table nested files are handled by a separate
  // parent-resolution helper below.
  return syncInfoFromPath(node.path, { includeQuoteTable: false, skipProperties: false });
}

/** Get sync info for a quote-table folder node (e.g. quote-tables/MyTable). */
function getQuoteTableSyncInfo(node) {
  if (node.type !== "folder") return null;
  const parts = node.path.split("/");
  // Must be exactly quote-tables/{TableName} (depth 2)
  if (parts.length === 2 && parts[0] === "quote-tables") {
    return { kind: "quote-tables", name: parts[1], apiKind: "quote-tables" };
  }
  return null;
}

/** Get the parent quote-table sync status for any node nested inside quote-tables/{Table}/... */
function getParentQuoteTableStatus(node) {
  const parts = node.path.split("/");
  // e.g. quote-tables/MyTable/actions/Foo.py → parts[0]="quote-tables", parts[1]="MyTable"
  if (parts.length >= 3 && parts[0] === "quote-tables") {
    return getSyncStatus("quote-tables", parts[1]);
  }
  return null;
}

/** Get pull info for a file nested inside a quote-table folder. */
function getParentQuoteTablePullInfo(node) {
  const parts = node.path.split("/");
  if (parts.length >= 3 && parts[0] === "quote-tables") {
    return { kind: "quote-tables", name: parts[1], apiKind: "quote-tables" };
  }
  return null;
}

// ---------------------------------------------------------------------------
// File tree filter
// ---------------------------------------------------------------------------

export function filterFileTree(query) {
  const q = query.trim().toLowerCase();
  const rows = document.querySelectorAll('#fileTree .tree-row');
  if (!q) {
    rows.forEach(r => { r.hidden = false; });
    // Also show all groups
    document.querySelectorAll('#fileTree .tree-group').forEach(g => { g.hidden = false; });
    // Show all list items and nested lists
    document.querySelectorAll('#fileTree li').forEach(li => { li.hidden = false; });
    document.querySelectorAll('#fileTree .tree-ul').forEach(ul => { ul.hidden = false; });
    return;
  }
  // Hide non-matching file rows
  rows.forEach(r => {
    const name = r.querySelector('.tree-name')?.textContent?.toLowerCase() || '';
    const isFolder = r.nextElementSibling && r.nextElementSibling.classList.contains('tree-ul');
    if (!isFolder) {
      const match = name.includes(q);
      r.hidden = !match;
      if (r.parentElement && r.parentElement.tagName === 'LI') {
        r.parentElement.hidden = !match;
      }
    }
  });
  // Show folders that have visible children, hide those that don't
  document.querySelectorAll('#fileTree .tree-ul .tree-ul').forEach(childUl => {
    const hasVisible = [...childUl.querySelectorAll(':scope > li')].some(li => !li.hidden);
    childUl.hidden = !hasVisible;
    // The folder row is the previous sibling of this UL, both inside the parent LI
    const parentLi = childUl.parentElement;
    if (parentLi && parentLi.tagName === 'LI') {
      const folderRow = parentLi.querySelector(':scope > .tree-row');
      if (folderRow) {
        folderRow.hidden = !hasVisible;
        parentLi.hidden = !hasVisible;
      }
    }
  });
}

// ---------------------------------------------------------------------------
// File tree
// ---------------------------------------------------------------------------

export function renderFileTree() {
  if (!refs.fileTree) return;
  if (!state.fileTree) {
    refs.fileTree.innerHTML = `<div class="tree-placeholder">Loading files...</div>`;
    return;
  }
  const ul = document.createElement("ul");
  ul.className = "tree-ul";
  renderTreeNodes(ul, state.fileTree.children || [], "");
  refs.fileTree.innerHTML = "";
  refs.fileTree.appendChild(ul);
  highlightActiveFileInTree();
  applySelectionClasses();
  // Drop selection entries for files that disappeared from the tree.
  const validPaths = new Set(
    [...refs.fileTree.querySelectorAll('.tree-row[data-path]')].map(r => r.dataset.path)
  );
  selection.prune(validPaths);
}

export function applySelectionClasses() {
  const container = refs.fileTree;
  if (!container) return;
  container.querySelectorAll('.tree-row.selected').forEach(r => r.classList.remove('selected'));
  for (const path of selection.getSelected()) {
    const row = container.querySelector(`.tree-row[data-path="${CSS.escape(path)}"]`);
    if (row) row.classList.add('selected');
  }
  container.classList.toggle('file-tree-multiselect', selection.size() > 1);
}

function renderTreeNodes(parentUl, nodes, parentPath) {
  for (const node of nodes) {
    const li = document.createElement("li");

    if (node.type === "folder") {
      const isOpen = state.openFolders.has(node.path);
      const row = document.createElement("div");
      row.className = "tree-row";

      // Quote-table table folders get sync badges (direct or propagated from parent)
      const qtInfo = getQuoteTableSyncInfo(node);
      const qtStatus = qtInfo ? getSyncStatus(qtInfo.kind, qtInfo.name) : null;
      const effectiveFolderStatus = qtStatus || getParentQuoteTableStatus(node);
      // Check for sync errors on this folder's kind
      const folderSyncError = (state.syncErrors || {})[node.name] || null;
      let folderBadge = "";
      if (folderSyncError) {
        folderBadge = `<span class="sync-badge sync-error" title="Sync failed: ${esc(folderSyncError)}">` +
          `<i class="ic ic-sm ic-alert-triangle"></i></span>`;
      } else if (effectiveFolderStatus) {
        folderBadge = syncBadgeHtml(effectiveFolderStatus);
      } else if (state.syncLoading && qtInfo) {
        folderBadge = '<span class="sync-badge loading"><span class="sync-spinner-sm"></span></span>';
      }

      const headerInfo = sectionHeaderInfo(node);
      const headerExtras = headerInfo ? `
        <span class="section-count">${headerInfo.count}</span>
        ${headerInfo.rollup ? `<span class="section-rollup">${esc(headerInfo.rollup)}</span>` : ""}
      ` : "";

      row.innerHTML = `
        <span class="panel-caret tree-caret ${isOpen ? "open" : ""}">&#9654;</span>
        <span class="tree-icon">${folderIconSvg()}</span>
        <span class="tree-name">${esc(node.name)}</span>
        ${headerExtras}
        ${folderBadge}
      `;

      // quote-tables folders intentionally render no folder-level action button (unlike ui-templates).
      if (node.name === "ui-templates") {
        const newBtn = document.createElement("button");
        newBtn.className = "btn btn-xs";
        newBtn.textContent = "+ New";
        newBtn.title = "Create new custom template from system default";
        newBtn.style.marginLeft = "auto";
        newBtn.addEventListener("click", (e) => {
          e.stopPropagation();
          openScaffoldDialog();
        });
        row.appendChild(newBtn);
      }

      row.addEventListener("click", () => {
        if (state.openFolders.has(node.path)) {
          state.openFolders.delete(node.path);
        } else {
          state.openFolders.add(node.path);
        }
        renderFileTree();
      });

      li.appendChild(row);

      if (isOpen && node.children?.length) {
        const childUl = document.createElement("ul");
        childUl.className = "tree-ul";
        const ARTIFACT_KINDS = new Set(["global-scripts", "custom-calculations", "ui-templates"]);
        if (ARTIFACT_KINDS.has(node.name)) {
          const pinned = [];
          const rest = [];
          for (const c of node.children) {
            const stem = (c.name || "").replace(/\.py$/, "").replace(/\.html$/, "");
            if (c.type === "file" && isArtifactPinned(node.name, stem)) pinned.push(c);
            else rest.push(c);
          }
          if (pinned.length) {
            renderTreeNodes(childUl, pinned, node.path);
            const sep = document.createElement("li");
            sep.innerHTML = `<div class="pinned-divider"></div>`;
            childUl.appendChild(sep);
          }
          renderTreeNodes(childUl, rest, node.path);
        } else {
          renderTreeNodes(childUl, node.children, node.path);
        }
        li.appendChild(childUl);
      }
    } else {
      const row = document.createElement("div");
      row.className = "tree-row";
      row.dataset.path = node.path;

      const isSystemTemplate = node.name.endsWith(".system.html");
      // Properties files are reference-only — no sync badges or actions
      const isPropertiesFile = node.path.includes("/properties/") || node.name === "properties.json";
      const syncInfo = isPropertiesFile ? null : getSyncableInfo(node);
      const syncStatus = syncInfo ? getSyncStatus(syncInfo.kind, syncInfo.name) : null;
      // Propagate parent quote-table status to child files (but not properties)
      const effectiveStatus = isPropertiesFile ? null : (syncStatus || getParentQuoteTableStatus(node));

      const badgeHtml = effectiveStatus
        ? syncBadgeHtml(effectiveStatus)
        : (state.syncLoading && syncInfo)
          ? '<span class="sync-badge loading"><span class="sync-spinner-sm"></span></span>'
          : "";

      // Script metadata (E/M/R) — only for global-scripts/*.py.
      // Carried via data attributes on the file icon so the hover popover and
      // a colored type-dot can pick it up without the standalone E/M/R chip.
      const scriptName = (node.path.startsWith("global-scripts/") && node.path.endsWith(".py"))
        ? node.name.replace(/\.py$/, "")
        : null;
      const scriptType = scriptName ? (state.scriptMeta?.[scriptName]?.type || "") : "";
      const iconAttrs = scriptName
        ? ` data-script-name="${escAttr(scriptName)}"${scriptType ? ` data-script-type="${scriptType}" title="${escAttr(SCRIPT_TYPE_TITLES[scriptType])}"` : ""}`
        : "";

      // Template chip + override glyph — only for ui-templates/*.html
      const templateChromeHtml = (() => {
        if (!node.path.startsWith("ui-templates/")) return "";
        const tn = node.name.replace(/\.system\.html$/, "").replace(/\.html$/, "");
        return templateChipHtml(tn) + templateOverrideHtml(tn, isSystemTemplate);
      })();

      row.innerHTML = `
        <span class="panel-caret tree-caret"></span>
        <span class="tree-icon"${iconAttrs}>${fileIconSvg(node.language)}</span>
        <span class="tree-name">${esc(node.name)}</span>
        ${templateChromeHtml}
        ${badgeHtml}
      `;

      // Determine action buttons: syncable files get their own sync actions;
      // quote-table child files get actions bound to the parent table.
      // Properties files are excluded — they are reference-only.
      const qtPullInfo = (!syncInfo && !isPropertiesFile) ? getParentQuoteTablePullInfo(node) : null;
      const qtParentStatus = qtPullInfo ? getSyncStatus(qtPullInfo.kind, qtPullInfo.name) : null;
      const actionTarget = syncInfo || qtPullInfo;  // whichever is available
      const actionStatus = syncInfo ? syncStatus : qtParentStatus;

      if (actionTarget) {
        // Hover cluster — only mention, to avoid accidental clicks on the
        // row's open action. Push/pull/diff/usage/stash live in the right-click
        // context menu + the opened file's header toolbar.
        const actions = document.createElement("div");
        actions.className = "tree-actions";
        const mentionBtn = createIconButton("mention", "Reference in chat", () => {
          const mentionName = syncInfo ? syncInfo.name : node.name.replace(/\.py$/, "").replace(/\.json$/, "");
          refs.promptInput.value += `@${mentionName} `;
          refs.promptInput.focus();
          refs.promptInput.dispatchEvent(new Event("input"));
        });
        actions.appendChild(mentionBtn);
        row.appendChild(actions);

        // Right-click context menu — operates on the current selection.
        // If the right-clicked row is not in the selection, replace selection
        // with that single row (matches every mainstream file explorer).
        row.addEventListener("contextmenu", (e) => {
          if (!selection.isSelected(node.path)) {
            selection.select(node.path, "replace");
          }
          _showFileTreeContextMenu(e);
        });
      }

      row.addEventListener("click", async (ev) => {
        if (ev.metaKey || ev.ctrlKey) {
          ev.preventDefault();
          selection.select(node.path, "toggle");
          return;
        }
        if (ev.shiftKey) {
          ev.preventDefault();
          selection.select(node.path, "range");
          return;
        }
        selection.select(node.path, "replace");

        // If file is remote-only, prompt user to pull from cloud first
        const fileStatus = effectiveStatus;
        if (fileStatus && fileStatus.direction === "remote_only") {
          const pullInfo = syncInfo || getParentQuoteTablePullInfo(node);
          if (pullInfo) {
            const ok = await confirmPullFromCloud(pullInfo.name);
            if (ok) await pullWithFeedback(pullInfo.apiKind, pullInfo.name);
            return;
          }
        }
        openFileTab(node.path, node.name, node.language);
      });

      li.appendChild(row);
    }

    parentUl.appendChild(li);
  }
}

function createIconButton(type, title, onClick) {
  const btn = document.createElement("button");
  btn.className = "btn-icon";
  btn.title = title;
  btn.innerHTML = actionIcon(type);
  btn.addEventListener("click", (e) => { e.stopPropagation(); onClick(); });
  return btn;
}

// Lucide icon name for each action type. Source of truth: tools/gen_icons.mjs.
const ACTION_LUCIDE = {
  mention: "at-sign",
  diff:    "git-pull-request",
  push:    "arrow-up-from-line",
  pull:    "arrow-down-to-line",
  usage:   "search",
  stash:   "archive",
  info:    "info",
  pin:     "pin",
  unpin:   "pin-off",
  run:     "play",
  validate:"check-circle",
  in_sync: "check",
  preview: "eye",
  overflow:"more-vertical",
  cloud:   "cloud",
  ide:     "terminal-square",
};

export function actionIcon(type) {
  const lucide = ACTION_LUCIDE[type];
  return lucide ? `<i class="ic ic-${lucide} ico"></i>` : "";
}

/**
 * Build the action list for a file tree row (context menu + file viewer header).
 * Returns an array of { type, label, run } — any subset may appear based on status.
 *
 * Exported so the file viewer header can reuse the same list when showing
 * sync-action buttons next to the filename.
 */
export function buildFileActions({ node, syncInfo, syncStatus, qtPullInfo, qtParentStatus }) {
  const actionTarget = syncInfo || qtPullInfo;
  if (!actionTarget) return [];
  const actionStatus = syncInfo ? syncStatus : qtParentStatus;
  const result = [];

  // 'More info...' — uniform across kinds. Routes to existing usage
  // endpoints + the bulk metadata maps loaded by loadFileTree.
  if (syncInfo) {
    result.push({
      type: "info",
      label: "More info…",
      run: async () => {
        const { showArtifactDetailsDialog, showUsageInfo } = await import('./dialogs.js');
        const meta = syncInfo.kind === "global-scripts"
          ? state.scriptMeta?.[syncInfo.name]
          : syncInfo.kind === "ui-templates"
            ? state.templateMeta?.[syncInfo.name]
            : null;
        // Show loading state immediately so the dialog opens without waiting for network.
        showUsageInfo(`Details: ${syncInfo.name}`,
          `<div style="text-align:center;padding:16px 0">` +
          `<div class="sync-spinner-sm" style="display:inline-block;width:20px;height:20px;margin-bottom:8px"></div>` +
          `<div style="color:var(--text-muted);font-size:13px">Loading details…</div></div>`);
        let usage = null;
        try {
          if (syncInfo.kind === "global-scripts") {
            usage = await api.get(`/api/scripts/${encodeURIComponent(syncInfo.name)}/usage`);
          } else if (syncInfo.kind === "custom-calculations") {
            usage = await api.calculationUsage(syncInfo.name);
          }
        } catch (e) {
          showArtifactDetailsDialog(syncInfo.kind, syncInfo.name, { meta, error: e.message });
          return;
        }
        showArtifactDetailsDialog(syncInfo.kind, syncInfo.name, { meta, usage });
      },
    });
  }

  // Reference in chat (@name)
  const mentionName = syncInfo
    ? syncInfo.name
    : (node?.name || '').replace(/\.py$/, "").replace(/\.json$/, "");
  result.push({
    type: "mention",
    label: "Reference in chat (@)",
    run: () => {
      if (!refs.promptInput) return;
      refs.promptInput.value += `@${mentionName} `;
      refs.promptInput.focus();
      refs.promptInput.dispatchEvent(new Event("input"));
    },
  });

  // Pin / Unpin — only on artifact rows (not properties/, mock-data/, etc).
  if (syncInfo && (syncInfo.kind === "global-scripts"
                   || syncInfo.kind === "custom-calculations"
                   || syncInfo.kind === "ui-templates")) {
    const pinned = isArtifactPinned(syncInfo.kind, syncInfo.name);
    result.push({
      type: pinned ? "unpin" : "pin",
      label: pinned ? "Unpin" : "Pin to top",
      run: () => {
        togglePinArtifact(syncInfo.kind, syncInfo.name);
        renderFileTree();
      },
    });
  }

  result.push({
    type: "diff",
    label: "View diff",
    run: () => openDiffForScript(actionTarget.name, actionTarget.apiKind),
  });

  if (syncInfo && (syncInfo.kind === "global-scripts" || syncInfo.kind === "custom-calculations")) {
    result.push({
      type: "usage",
      label: "View usage",
      run: () => viewUsageForKindAndName(syncInfo.kind, syncInfo.name, syncStatus),
    });
  }

  if (actionStatus && (actionStatus.direction === "local_only" || actionStatus.direction === "conflict")) {
    result.push({
      type: "push",
      label: "Push to cloud",
      run: async () => {
        const ok = await confirmPushToCloud(actionTarget.name, state.tenant);
        if (ok) await pushWithFeedback(actionTarget.apiKind, actionTarget.name);
      },
    });
  }

  if (actionStatus && actionStatus.direction === "local_only") {
    result.push({
      type: "stash",
      label: "Stash (move to .stash/)",
      run: async () => {
        const ok = await confirm("Stash File", `Move "${actionTarget.name}" to .stash/? You can restore it later.`);
        if (!ok) return;
        try {
          await api.syncStash(actionTarget.apiKind, actionTarget.name);
          await loadSyncStatuses(true);
          await loadFileTree();
        } catch (e) {
          alert(`Stash failed: ${e.message}`);
        }
      },
    });
  }

  if (actionStatus && (actionStatus.direction === "remote_only" || actionStatus.direction === "conflict")) {
    result.push({
      type: "pull",
      label: "Pull from cloud",
      run: async () => {
        const ok = await confirmPullFromCloud(actionTarget.name);
        if (ok) await pullWithFeedback(actionTarget.apiKind, actionTarget.name);
      },
    });
  }

  return result;
}

/**
 * Given a tenant-relative file path, return the context needed to build
 * file actions — used by the file viewer header to show sync buttons.
 */
export function buildFileActionsForPath(path) {
  if (!path) return [];
  const direct = syncInfoFromPath(path, { includeQuoteTable: false });
  const parent = !direct ? syncInfoFromPath(path, { includeQuoteTable: true }) : null;
  const syncInfo = direct;
  const syncStatus = direct ? getSyncStatus(direct.kind, direct.name) : null;
  const qtPullInfo = parent;
  const qtParentStatus = parent ? getSyncStatus(parent.kind, parent.name) : null;
  const node = { name: path.split('/').pop(), path, type: 'file' };
  return buildFileActions({ node, syncInfo, syncStatus, qtPullInfo, qtParentStatus });
}

/**
 * Build the action list for N≥1 selected file paths.
 * For N=1 delegates to buildFileActionsForPath (zero behavior change).
 * For N>1 returns the bulk-aware action list.
 */
export function buildBulkActions(paths) {
  if (!paths.length) return [];
  if (paths.length === 1) return buildFileActionsForPath(paths[0]);

  const entries = paths.map(p => {
    const info = syncInfoFromPath(p, { includeQuoteTable: false });
    if (!info) return null;
    const status = getSyncStatus(info.kind, info.name);
    return { path: p, info, status };
  }).filter(Boolean);

  const result = [];
  if (!entries.length) return result;

  // More info — combined summary
  result.push({
    type: 'info',
    label: `More info on ${entries.length} files…`,
    run: () => {
      const lines = entries.map(e => {
        const dir = e.status?.direction || 'unknown';
        const meta = e.info.kind === 'global-scripts' ? state.scriptMeta?.[e.info.name]
                   : e.info.kind === 'ui-templates' ? state.templateMeta?.[e.info.name]
                   : null;
        const extras = [];
        if (meta?.type) extras.push(`type ${meta.type}`);
        if (meta?.eventCount) extras.push(`${meta.eventCount} event(s)`);
        if (meta?.modifiedOn) extras.push(`modified ${meta.modifiedOn}`);
        if (meta?.pageName) extras.push(`page ${meta.pageName}`);
        const tail = extras.length ? ` — ${extras.join(' · ')}` : '';
        return `<div style="margin-bottom:6px">`
          + `<strong>${esc(e.info.name)}</strong> `
          + `<span style="color:var(--text-muted)">[${esc(e.info.kind)} · ${esc(dir)}]</span>`
          + `${esc(tail)}</div>`;
      });
      showUsageInfo(`Details: ${entries.length} files`, lines.join(''));
    },
  });

  // Reference in chat — append @a @b @c
  const mentionNames = entries.map(e => e.info.name);
  result.push({
    type: 'mention',
    label: `Reference ${mentionNames.length} files in chat (@)`,
    run: () => {
      if (!refs.promptInput) return;
      const tokens = mentionNames.map(n => `@${n}`).join(' ');
      refs.promptInput.value += tokens + ' ';
      refs.promptInput.focus();
      refs.promptInput.dispatchEvent(new Event('input'));
    },
  });

  // Pin / Unpin — smart toggle on pinnable kinds
  const PINNABLE = new Set(['global-scripts', 'custom-calculations', 'ui-templates']);
  const pinnable = entries.filter(e => PINNABLE.has(e.info.kind));
  if (pinnable.length) {
    const anyUnpinned = pinnable.some(e => !isArtifactPinned(e.info.kind, e.info.name));
    result.push({
      type: anyUnpinned ? 'pin' : 'unpin',
      label: anyUnpinned
        ? `Pin ${pinnable.length} file${pinnable.length > 1 ? 's' : ''} to top`
        : `Unpin ${pinnable.length} file${pinnable.length > 1 ? 's' : ''}`,
      run: () => {
        for (const e of pinnable) {
          const isPinned = isArtifactPinned(e.info.kind, e.info.name);
          if (anyUnpinned && !isPinned) togglePinArtifact(e.info.kind, e.info.name);
          else if (!anyUnpinned && isPinned) togglePinArtifact(e.info.kind, e.info.name);
        }
        renderFileTree();
      },
    });
  }

  // Push: any local_only or conflict
  const pushable = entries.filter(e => e.status && (e.status.direction === 'local_only' || e.status.direction === 'conflict'));
  if (pushable.length) {
    result.push({
      type: 'push',
      label: `Push ${pushable.length} file${pushable.length > 1 ? 's' : ''} to cloud`,
      run: async () => {
        const ok = await confirm('Push to cloud', `Push ${pushable.length} file(s) to cloud?`);
        if (!ok) return;
        await bulkSync('push', pushable.map(e => ({ kind: e.info.kind, name: e.info.name })));
      },
    });
  }

  // Pull: any remote_only or conflict
  const pullable = entries.filter(e => e.status && (e.status.direction === 'remote_only' || e.status.direction === 'conflict'));
  if (pullable.length) {
    result.push({
      type: 'pull',
      label: `Pull ${pullable.length} file${pullable.length > 1 ? 's' : ''} from cloud`,
      run: async () => {
        const ok = await confirm('Pull from cloud', `Pull ${pullable.length} file(s) from cloud?`);
        if (!ok) return;
        await bulkSync('pull', pullable.map(e => ({ kind: e.info.kind, name: e.info.name })), { reloadTree: true });
      },
    });
  }

  // Stash: any local_only
  const stashable = entries.filter(e => e.status && e.status.direction === 'local_only');
  if (stashable.length) {
    result.push({
      type: 'stash',
      label: `Stash ${stashable.length} file${stashable.length > 1 ? 's' : ''}`,
      run: async () => {
        const ok = await confirm('Stash files', `Move ${stashable.length} file(s) to .stash/? You can restore them later.`);
        if (!ok) return;
        await bulkStash(stashable.map(e => ({ kind: e.info.kind, name: e.info.name })));
      },
    });
  }

  return result;
}

// -------------------------------------------------------------------------
// Tree context menu
// -------------------------------------------------------------------------

let _ctxMenuEl = null;

const _isCtxMenuOpen = () => !!_ctxMenuEl && !_ctxMenuEl.hidden;

function _ensureCtxMenu() {
  if (_ctxMenuEl) return _ctxMenuEl;
  _ctxMenuEl = document.createElement('div');
  _ctxMenuEl.className = 'tree-ctx-menu';
  _ctxMenuEl.hidden = true;
  document.body.appendChild(_ctxMenuEl);

  document.addEventListener('click', (e) => {
    if (_isCtxMenuOpen() && !_ctxMenuEl.contains(e.target)) _hideCtxMenu();
  }, true);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && _isCtxMenuOpen()) _hideCtxMenu();
  });
  window.addEventListener('scroll', () => { if (_isCtxMenuOpen()) _hideCtxMenu(); }, true);
  return _ctxMenuEl;
}

function _hideCtxMenu() {
  if (_ctxMenuEl) _ctxMenuEl.hidden = true;
}

function _showFileTreeContextMenu(e) {
  e.preventDefault();
  e.stopPropagation();
  const menu = _ensureCtxMenu();
  menu.innerHTML = '';

  const paths = selection.getSelected();
  const actions = buildBulkActions(paths);
  if (!actions.length) { _hideCtxMenu(); return; }

  for (const a of actions) {
    const btn = document.createElement('button');
    btn.className = 'tree-ctx-item';
    btn.innerHTML = `<span class="tree-ctx-icon">${actionIcon(a.type)}</span><span class="tree-ctx-label">${esc(a.label)}</span>`;
    btn.addEventListener('click', (ev) => {
      ev.stopPropagation();
      _hideCtxMenu();
      a.run();
    });
    menu.appendChild(btn);
  }

  menu.hidden = false;
  const mw = menu.offsetWidth || 200;
  const mh = menu.offsetHeight || 100;
  menu.style.left = Math.min(e.clientX, window.innerWidth - mw - 8) + 'px';
  menu.style.top = Math.min(e.clientY, window.innerHeight - mh - 8) + 'px';
}

// ---------------------------------------------------------------------------
// Data loading
// ---------------------------------------------------------------------------

export async function loadFileTree() {
  if (!loadFileTree._wired) {
    loadFileTree._wired = true;
    selection.setVisiblePathsProvider(() => {
      const tree = refs.fileTree || document.getElementById('fileTree');
      if (!tree) return [];
      return [...tree.querySelectorAll('.tree-row[data-path]')].map(r => r.dataset.path);
    });
    selection.onChange(applySelectionClasses);
    document.addEventListener('keydown', (e) => {
      const t = e.target;
      const inEditable = t && (
        t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable
      );
      if (e.key === 'Escape') {
        if (selection.size() > 0) selection.clear();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && (e.key === 'a' || e.key === 'A')) {
        if (inEditable) return;
        if (!selection.getAnchor()) return;
        e.preventDefault();
        selection.selectSiblingsOfAnchor();
      }
    });
    const treeEl = document.getElementById('fileTree');
    if (treeEl) {
      treeEl.addEventListener('click', (ev) => {
        if (ev.target === treeEl) selection.clear();
      });
    }
  }
  if (state.syncStatusMap.size === 0) state.syncLoading = true;
  setPanelLoading('explorer', true);
  try {
    const result = await api.fileTree();
    state.fileTree = result.tree;
    // Fire-and-forget metadata fetches; they may resolve after the first
    // render, in which case renderFileTree() is called again on completion.
    Promise.allSettled([
      api.scriptsMetadata().then(m => { state.scriptMeta = m || {}; renderFileTree(); }),
      api.templatesMetadata().then(m => { state.templateMeta = m || {}; renderFileTree(); }),
    ]);
    renderFileTree();
  } catch (e) {
    if (refs.fileTree) refs.fileTree.innerHTML = `<div class="tree-placeholder">Failed to load files: ${esc(e.message)}</div>`;
  } finally {
    setPanelLoading('explorer', false);
  }
}

export async function loadSyncStatuses(refresh = false) {
  // Single writer of the sync signals. Consumers (file tree, nav badges, sync
  // panel, status bar, diff list) subscribe in initSyncLiveBindings() and
  // re-render automatically — no manual fan-out, so nothing goes stale.
  state.syncLoading = true;
  setPanelLoading('explorer', true);
  setPanelLoading('sync-status', true);
  try {
    const result = await api.syncStatus(null, refresh);
    state.syncStatuses = result.items;
    state.syncErrors = result.sync_errors || {};
    // Assign a FRESH Map (identity change) so subscribers are notified —
    // mutating the existing Map in place would not fire the signal.
    const map = new Map();
    for (const item of result.items) {
      map.set(`${item.kind}/${item.name}`, item);
    }
    state.syncStatusMap = map;
    state.syncStatusLoadFailed = false;
  } catch (e) {
    console.warn("Sync status unavailable:", e.message);
    if (refs.syncSummary) refs.syncSummary.textContent = "Sync unavailable";
    state.syncStatusLoadFailed = true;
  } finally {
    state.syncLoading = false;
    setPanelLoading('explorer', false);
    setPanelLoading('sync-status', false);
    renderWorkspaceTabs();
  }
}

let _syncBindingsWired = false;

/**
 * Wire the reactive sync bindings once (called from init() after panels exist).
 * Each consumer subscribes to the sync signals via an effect, so a single
 * loadSyncStatuses() (or any state.sync* write — e.g. from the file-watcher)
 * keeps every surface live without manual re-render calls.
 */
export function initSyncLiveBindings() {
  if (_syncBindingsWired) return;
  _syncBindingsWired = true;

  // File tree depends on the tree, sync map, metadata, and loading flag.
  effect(() => {
    signals.fileTree.get();
    signals.syncStatusMap.get();
    signals.scriptMeta.get();
    signals.templateMeta.get();
    signals.syncLoading.get();
    renderFileTree();
  });

  // Top-nav badges, sync panel, and status bar derive from sync status.
  effect(() => { signals.syncStatuses.get(); _updateSyncBadges(); });
  effect(() => { signals.syncStatuses.get(); renderSyncPanel(); });
  effect(() => { signals.syncStatuses.get(); signals.tenant.get(); updateStatusBarSync(); });
}

// ---------------------------------------------------------------------------
// Top-nav sync badges
// ---------------------------------------------------------------------------

function _updateSyncBadges() {
  const statuses = state.syncStatuses || [];
  const synced = statuses.filter(s => s.direction === 'in_sync').length;
  const localOnly = statuses.filter(s => s.direction === 'local_only').length;
  const remoteOnly = statuses.filter(s => s.direction === 'remote_only').length;
  const conflicts = statuses.filter(s => s.direction === 'conflict').length;

  const elSync     = document.getElementById('syncBadgeSync');
  const elLocal    = document.getElementById('syncBadgeLocal');
  const elRemote   = document.getElementById('syncBadgeRemote');
  const elConflict = document.getElementById('syncBadgeConflict');

  // Nav badges keep their Lucide <i> child (set in index.html); we update only the count <span>.
  const setBadge = (root, n) => {
    if (!root) return;
    const numEl = root.querySelector('.sync-badge-n');
    if (numEl) numEl.textContent = String(n);
    root.hidden = n === 0;
  };
  setBadge(elSync, synced);
  setBadge(elLocal, localOnly);
  setBadge(elRemote, remoteOnly);
  setBadge(elConflict, conflicts);
}

export function renderSyncSummary() {
  // No-op kept for API compatibility — callers still call this.
}

export async function checkHealth() {
  try {
    await api.health();
    state.healthOk = true;
  } catch {
    state.healthOk = false;
  }
}

/**
 * Highlight the file tree row matching the currently active workspace tab.
 * Call this whenever the active tab changes.
 */
export function highlightActiveFileInTree(scroll = false) {
  const container = refs.fileTree;
  if (!container) return;
  container.querySelectorAll('.tree-row.active-file').forEach(r => r.classList.remove('active-file'));
  if (!state.activeWorkspaceTab) return;
  const tab = state.workspaceTabs.find(t => t.id === state.activeWorkspaceTab);
  if (!tab?.path) return;

  let row = container.querySelector(`.tree-row[data-path="${CSS.escape(tab.path)}"]`);

  // If the row isn't in the DOM, expand parent folders so it becomes visible.
  // Safe from infinite loops: second call finds all folders already open, so needsRerender stays false.
  if (!row) {
    const parts = tab.path.split('/');
    let needsRerender = false;
    for (let i = 1; i < parts.length; i++) {
      const folderPath = parts.slice(0, i).join('/');
      if (!state.openFolders.has(folderPath)) {
        state.openFolders.add(folderPath);
        needsRerender = true;
      }
    }
    if (needsRerender) {
      renderFileTree();
      return;
    }
  }

  if (row) {
    row.classList.add('active-file');
    if (scroll) row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }
}

// ---------------------------------------------------------------------------
// Usage dialog (uses dedicated #usageDialog — never mutates #confirmDialog)
// ---------------------------------------------------------------------------

function _renderItems(items) {
  return items.map(i => typeof i === "object" && i.name ? esc(i.name) : esc(String(i))).join(", ");
}

/**
 * Fetch + display usage for a script or calculation. Used by both the file-tree
 * right-click menu and the header View menu.
 * @param {string} kind — "global-scripts" or "custom-calculations"
 * @param {string} name
 * @param {object} [syncStatus] — optional; if direction === "local_only", show the
 *                                push prompt instead of fetching cloud usage.
 */
export async function viewUsageForKindAndName(kind, name, syncStatus) {
  if (kind !== "global-scripts" && kind !== "custom-calculations") {
    return;
  }
  // Map kind -> apiKind. The right-click menu passes apiKind via syncInfo,
  // but the View menu has only the kind. Derive apiKind from kind.
  const apiKind = kind === "global-scripts" ? "global-scripts" : "custom-calculations";
  if (syncStatus && syncStatus.direction === "local_only") {
    showUsageLocalOnly(name, apiKind);
    return;
  }
  showUsageLoading(name);
  try {
    const usage = kind === "global-scripts"
      ? await api.get(`/api/scripts/${encodeURIComponent(name)}/usage`)
      : await api.calculationUsage(name);
    if (kind === "global-scripts") showUsageDialog(name, usage);
    else showCalcUsageDialog(name, usage);
  } catch (e) {
    showUsageError(name, e.message);
  }
}

function showUsageLoading(name) {
  showUsageInfo(`Usage: ${name}`, `
    <div style="text-align:center;padding:16px 0">
      <div class="sync-spinner-sm" style="display:inline-block;width:20px;height:20px;margin-bottom:8px"></div>
      <div style="color:var(--text-muted);font-size:13px">Loading usage data from SAP CPQ...</div>
    </div>
  `);
}

function showUsageLocalOnly(name, apiKind) {
  const html = `
    <p>This file exists only locally and has not been pushed to SAP CPQ yet.</p>
    <p style="color:var(--text-muted);font-size:13px">Usage data is only available for files that exist on the cloud.</p>
  `;
  showUsagePushPrompt(name, apiKind, html, async () => {
    await loadSyncStatuses();
    renderFileTree();
  });
}

function showUsageError(name, message) {
  showUsageInfo(`Usage: ${name}`,
    `<div style="color:var(--error)">Failed to load usage: ${esc(message)}</div>`
  );
}

function showUsageDialog(scriptName, usage) {
  const products = usage.products || [];
  const attrs = usage.productAttributes || [];
  const events = usage.systemEvents || [];
  const scripts = usage.scripts || [];
  const actions = usage.customActions || [];
  const calcs = usage.customQuoteCalculations || [];

  const sections = [];

  if (products.length) {
    sections.push(`<strong>Products:</strong> ${products.map(p => esc(p)).join(", ")}`);
  }
  if (attrs.length) {
    sections.push(`<strong>Product Attributes:</strong><br>${attrs.map(a => `  ${esc(a.key)} → ${esc(a.value)}`).join("<br>")}`);
  }
  if (events.length) {
    sections.push(`<strong>System Events:</strong> ${events.map(e => esc(e)).join(", ")}`);
  }
  if (scripts.length) {
    sections.push(`<strong>Used by Global Scripts:</strong> ${_renderItems(scripts)}`);
  }
  if (actions.length) {
    sections.push(`<strong>Custom Actions:</strong> ${_renderItems(actions)}`);
  }
  if (calcs.length) {
    sections.push(`<strong>Used by Custom Calculations:</strong> ${_renderItems(calcs)}`);
  }

  if (!sections.length) {
    sections.push("<em>No usage found — this script is not attached anywhere.</em>");
  }

  showUsageInfo(`Usage: ${scriptName}`, sections.join("<br><br>"));
}

function showCalcUsageDialog(calcName, usage) {
  const modules = usage.importedModules || [];
  const fields = usage.attachedToFields || [];

  const sections = [];

  // Field bindings
  if (fields.length) {
    sections.push(`<strong>Attached to Fields:</strong> ${fields.map(f => esc(f.fieldName || String(f.fieldId))).join(", ")}`);
  }

  // Pipeline position
  const info = [];
  if (usage.rank != null) info.push(`Rank <strong>${usage.rank}</strong>`);
  if (usage.rowTypeLabel) info.push(`Row Type <strong>${esc(usage.rowTypeLabel)}</strong>`);
  if (usage.isActive != null) info.push(usage.isActive ? "Active" : "<em>Inactive</em>");
  if (usage.executeOnAddUpdateItem != null) {
    info.push(usage.executeOnAddUpdateItem ? "Executes on item add/update" : "Manual execution only");
  }
  if (info.length) {
    sections.push(`<strong>Pipeline:</strong> ${info.join(" · ")}`);
  }

  if (modules.length) {
    sections.push(`<strong>Imported Modules:</strong> ${modules.map(m => esc(m)).join(", ")}`);
  }

  if (!modules.length && !info.length && !fields.length) {
    sections.push("<em>No usage info available.</em>");
  }

  showUsageInfo(`Usage: ${calcName}`, sections.join("<br><br>"));
}

// ---------------------------------------------------------------------------
// Hover popover — anchored to script glyph + template slot chip
// ---------------------------------------------------------------------------

let _hoverEl = null;
let _hoverTimer = null;

function _ensureHoverEl() {
  if (_hoverEl) return _hoverEl;
  _hoverEl = document.createElement("div");
  _hoverEl.className = "artifact-hover-popover";
  _hoverEl.hidden = true;
  document.body.appendChild(_hoverEl);
  return _hoverEl;
}

function _hideHover() {
  if (_hoverTimer) { clearTimeout(_hoverTimer); _hoverTimer = null; }
  if (_hoverEl) _hoverEl.hidden = true;
}

function _showHoverNear(target, html) {
  const el = _ensureHoverEl();
  el.innerHTML = html;
  el.hidden = false;
  const r = target.getBoundingClientRect();
  const w = el.offsetWidth || 240;
  const h = el.offsetHeight || 100;
  let x = r.left;
  let y = r.bottom + 6;
  if (x + w > window.innerWidth - 8) x = window.innerWidth - w - 8;
  if (y + h > window.innerHeight - 8) y = r.top - h - 6;
  el.style.left = `${Math.max(8, x)}px`;
  el.style.top = `${Math.max(8, y)}px`;
}

function _hoverContentForScript(name) {
  const m = state.scriptMeta?.[name] || {};
  const types = [];
  if (m.isRest)   types.push("REST-exposed");
  if (m.eventCount) types.push(`${m.eventCount} event(s)`);
  if (m.isModule) types.push("module");
  if (!types.length) types.push("Unattached");
  return `<div class="hover-title">${esc(name)}</div>`
    + `<div class="hover-line"><b>Type:</b> ${esc(types.join(" · "))}</div>`
    + `<div class="hover-line"><b>Active:</b> ${m.isActive ? "Yes" : "No"}</div>`
    + (m.modifiedOn ? `<div class="hover-line hover-muted">Modified ${esc(m.modifiedOn)}</div>` : "");
}

function _hoverContentForTemplate(name) {
  const m = state.templateMeta?.[name] || {};
  if (!m.pageName && !m.slotName) return "";
  const slot = (m.slotName || "").replace(/^Templates\//, "");
  return `<div class="hover-title">${esc(name)}</div>`
    + `<div class="hover-line"><b>Page:</b> ${esc(m.pageName || "")}</div>`
    + `<div class="hover-line"><b>Slot:</b> ${esc(slot)}</div>`
    + `<div class="hover-line"><b>State:</b> ${m.isOverride ? "Customizes system default" : "Pure custom"}</div>`
    + (m.modifiedBy ? `<div class="hover-line hover-muted">Modified by ${esc(m.modifiedBy)}</div>` : "");
}

function _bindHoverFor(selector, contentFn, nameAttr) {
  document.addEventListener("mouseenter", (e) => {
    const el = e.target.closest && e.target.closest(selector);
    if (!el) return;
    if (_hoverTimer) clearTimeout(_hoverTimer);
    _hoverTimer = setTimeout(() => {
      const name = el.getAttribute(nameAttr)
        || el.closest(".tree-row")?.querySelector(".tree-name")?.textContent
            ?.replace(/\.py$/, "").replace(/\.html$/, "");
      if (!name) return;
      const html = contentFn(name);
      if (html) _showHoverNear(el, html);
    }, 400);
  }, true);
  document.addEventListener("mouseleave", (e) => {
    if (e.target.closest && e.target.closest(selector)) _hideHover();
  }, true);
}

let _hoverBound = false;
export function bindArtifactHovers() {
  if (_hoverBound) return;
  _hoverBound = true;
  _bindHoverFor(".tree-icon[data-script-name]", _hoverContentForScript, "data-script-name");
  _bindHoverFor(".template-slot-chip", _hoverContentForTemplate, "data-name");
  window.addEventListener("scroll", _hideHover, true);
}
